
<div class="w-full space-y-6">
  <!-- Header Section -->
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-2xl font-bold text-gray-800">Dashboard</h1>
      <p class="text-gray-600 mt-1">Welcome back, {{ currentUser.fullName }}!</p>
    </div>
  </div>

  <!-- Stats Cards -->
  <div class="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-3 gap-6">
    <!-- Total Users Card -->
    <div class="bg-white p-6 rounded-xl shadow-lg border border-blue-100">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Total Users</p>
          <p class="text-2xl font-bold text-gray-800">{{ stats.totalUsers }}</p>
          <div class="flex items-center mt-2">
            <p class="text-xs text-green-600">{{ getUserActivityPercentage() }}% active</p>
            <p-progressBar 
              [value]="getUserActivityPercentage()" 
              [showValue]="false"
              styleClass="ml-2 h-1 w-16">
            </p-progressBar>
          </div>
        </div>
        <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-users text-blue-600 text-xl"></i>
        </div>
      </div>
    </div>

    <!-- Active Users Card -->
    <div class="bg-white p-6 rounded-xl shadow-lg border border-green-100">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Active Users</p>
          <p class="text-2xl font-bold text-green-600">{{ stats.activeUsers }}</p>
          <p class="text-xs text-gray-500 mt-2">Last 30 days</p>
        </div>
        <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-check-circle text-green-600 text-xl"></i>
        </div>
      </div>
    </div>

    <!-- Total Subscriptions Card -->
    <div class="bg-white p-6 rounded-xl shadow-lg border border-purple-100">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Total Subscriptions</p>
          <p class="text-2xl font-bold text-purple-600">{{ stats.totalSubscriptions }}</p>
          <div class="flex items-center mt-2">
            <p class="text-xs text-green-600">{{ getSubscriptionActivityPercentage() }}% active</p>
            <p-progressBar 
              [value]="getSubscriptionActivityPercentage()" 
              [showValue]="false"
              styleClass="ml-2 h-1 w-16">
            </p-progressBar>
          </div>
        </div>
        <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-credit-card text-purple-600 text-xl"></i>
        </div>
      </div>
    </div>

  
  </div>

  <!-- Admin-only Stats (shown only for admin users) -->
  <div *ngIf="isAdmin" class="grid grid-cols-1 md:grid-cols-2 gap-6">
    <!-- Total Accounts Card -->
    <div class="bg-white p-6 rounded-xl shadow-lg border border-indigo-100">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Total Accounts</p>
          <p class="text-2xl font-bold text-indigo-600">{{ stats.totalAccounts }}</p>
          <p class="text-xs text-gray-500 mt-2">Business accounts</p>
        </div>
        <div class="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-building text-indigo-600 text-xl"></i>
        </div>
      </div>
    </div>

    <!-- Pending Invitations Card -->
    <div class="bg-white p-6 rounded-xl shadow-lg border border-yellow-100">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Pending Invitations</p>
          <p class="text-2xl font-bold text-yellow-600">{{ stats.pendingInvitations }}</p>
          <p class="text-xs text-gray-500 mt-2">Awaiting response</p>
        </div>
        <div class="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-envelope text-yellow-600 text-xl"></i>
        </div>
      </div>
    </div>

 
  </div>


  <!-- Recent Activity and Data Section -->
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <!-- Recent Activity -->
    <div class="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      <div class="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
        <h3 class="text-lg font-semibold text-gray-800">Recent Activity</h3>
      </div>
      <div class="p-6">
        <div class="space-y-4">
          <div 
            *ngFor="let activity of recentActivities" 
            class="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <div class="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
              <i [class]="activity.icon + ' ' + activity.color"></i>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm text-gray-800">{{ activity.message }}</p>
              <p class="text-xs text-gray-500 mt-1">{{ formatDateTime(activity.timestamp) }}</p>
            </div>
          </div>
        </div>
        <div class="mt-4 pt-4 border-t border-gray-100">
          <button class="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium">
            View All Activity
          </button>
        </div>
      </div>
    </div>

    <!-- Recent Users -->
    <div class="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      <div class="px-6 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-100">
        <h3 class="text-lg font-semibold text-gray-800">Recent Users</h3>
      </div>
      <div class="p-6">
        <div class="space-y-4">
          <div 
            *ngFor="let user of recentUsers" 
            class="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
          >
            <div class="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
              {{ getInitials(user.firstName, user.lastName) }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center space-x-2">
                <p class="text-sm font-medium text-gray-800">{{ user.firstName }} {{ user.lastName }}</p>
                <span [class]="'px-2 py-1 rounded-full text-xs font-medium ' + getRoleBadgeClass(user.userRole)">
                  {{ user.userRole }}
                </span>
              </div>
              <p class="text-xs text-gray-500">{{ user.userEmail }}</p>
              <p class="text-xs text-gray-400">Last login: {{ formatDateTime(user.lastLogin || '') }}</p>
            </div>
          </div>
        </div>
        <div class="mt-4 pt-4 border-t border-gray-100">
          <button class="w-full text-center text-sm text-green-600 hover:text-green-800 font-medium">
            View All Users
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Admin-only Recent Subscriptions -->
  <div *ngIf="isAdmin" class="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
    <div class="px-6 py-4 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-100">
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-semibold text-gray-800">Recent Subscriptions</h3>
        <button class="text-sm text-purple-600 hover:text-purple-800 font-medium">
          View All Subscriptions
        </button>
      </div>
    </div>
    <div class="p-6">
      <div class="space-y-4">
        <div 
          *ngFor="let subscription of recentSubscriptions" 
          class="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <div class="flex items-center space-x-4">
            <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <i class="pi pi-credit-card text-purple-600 text-xl"></i>
            </div>
            <div>
              <h4 class="font-semibold text-gray-800">{{ subscription.name }}</h4>
              <p class="text-sm text-gray-600">{{ subscription.product }} • {{ subscription.billingCycle }}</p>
              <div class="flex items-center space-x-2 mt-1">
                <span [class]="'px-2 py-1 rounded-full text-xs font-medium ' + getStatusBadgeClass(subscription.status || 'inactive')">
                  {{ subscription.status || 'inactive' }}
                </span>
                <span class="text-xs text-gray-500">
                  Next billing: {{ formatDate(subscription.nextBillingDate || '') }}
                </span>
              </div>
            </div>
          </div>
          <div class="text-right">
            <p class="font-semibold text-gray-800">{{ formatCurrency(subscription.baseFee) }}</p>
            <p class="text-xs text-gray-500">per {{ subscription.billingCycle }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Quick Actions -->
  <div class="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
    <h3 class="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <button class="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors">
        <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-user-plus text-blue-600"></i>
        </div>
        <div class="text-left">
          <p class="font-medium text-gray-800">Add User</p>
          <p class="text-xs text-gray-500">Invite new team member</p>
        </div>
      </button>

      <button class="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-green-50 hover:border-green-300 transition-colors">
        <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-plus-circle text-green-600"></i>
        </div>
        <div class="text-left">
          <p class="font-medium text-gray-800">New Subscription</p>
          <p class="text-xs text-gray-500">Add subscription plan</p>
        </div>
      </button>

      <button class="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-colors">
        <div class="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-cog text-purple-600"></i>
        </div>
        <div class="text-left">
          <p class="font-medium text-gray-800">Settings</p>
          <p class="text-xs text-gray-500">Account configuration</p>
        </div>
      </button>

      <button class="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-orange-50 hover:border-orange-300 transition-colors">
        <div class="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-chart-bar text-orange-600"></i>
        </div>
        <div class="text-left">
          <p class="font-medium text-gray-800">Reports</p>
          <p class="text-xs text-gray-500">View analytics</p>
        </div>
      </button>
    </div>
  </div>
</div>
